import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import { useDOA } from '../hooks/useDOA';
import { useEntities } from '../hooks/useEntities';

const API = `${API_V1}/ap`;
const PAGE_SIZE = 50;

interface InvoiceLine { id: number; line_number: number; po_number: string; po_line_number: number; description: string; quantity: number; unit_price: number; line_amount: number; gl_account: string; cost_center: string; }
interface MatchResult { match_status: string; invoice_amount: number; expected_amount: number; po_amount: number; gr_count: number; gr_total_qty: number; po_total_qty: number; price_variance_pct: number; qty_variance_pct: number; details: string; variances: any[]; line_matches?: any[]; }
interface LinkedGR { id: number; gr_number: string; status: string; quantity: number; amount: number; gr_date: string; }
interface PaymentLink { run_id: number; status: string; item_status: string; run_date: string; }
interface Invoice { id: number; invoice_number: string; invoice_date: string; vendor_id: string; vendor_name: string; po_number: string; gross_amount: number; currency: string; lifecycle_status: string; gl_account: string; entity_code: string; version: number; created_at: string; approved_by: number; approved_at: string; approval_level: string; match_status: string; match_result: MatchResult; price_variance_pct: number; qty_variance_pct: number; lines: InvoiceLine[]; payment_terms: string; due_date: string; posting_date: string; linked_grs?: LinkedGR[]; payment?: PaymentLink; has_unlinked_capex?: boolean; }
interface Summary { total: number; received: number; coded: number; matched: number; pending_approval: number; approved: number; posted: number; paid: number; pending_amount: number; }

export default function APInvoices() {
  const { canApprove } = useDOA();
  const navigate = useNavigate();
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedInv, setExpandedInv] = useState<Invoice | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editInv, setEditInv] = useState<Invoice | null>(null);
  const [editForm, setEditForm] = useState({ vendor_id: '', entity_code: '', gl_account: '', payment_terms: '', due_date: '' });
  const [editLines, setEditLines] = useState<{id: number; gl_account: string}[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchResult, setBatchResult] = useState<any>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ invoice_number: '', invoice_date: '', vendor_id: '', vendor_name: '', po_number: '', gross_amount: '', currency: 'USD', entity_code: '', payment_terms: '' });

  // Success toasts auto-dismiss after 4s. Error toasts stay until the
  // user clicks the X -- per Albert's 2026-05-19 feedback "the user
  // cannot guess", actionable failures need to be visible long enough
  // to read + copy + act on (vs a 4-second flash they miss while
  // looking at DevTools).
  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    if (ok) setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/invoices?${p}`, entity)),
        authFetch(withEntityParam(`${API}/invoices/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setInvoices(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = { ...form, gross_amount: parseFloat(form.gross_amount) || 0 };
      const res = await authFetch(`${API}/invoices`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Invoice created', true); setShowCreate(false); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleAction = async (id: number, action: string, version?: number) => {
    try {
      const body = version ? { expected_version: version } : {};
      const res = await authFetch(`${API}/invoices/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (res.ok) {
        const d = await res.json();
        const warnings = d.gl_warnings || [];
        if (warnings.length > 0) {
          showToast(`${d.action || action}: ${warnings[0]}`, false);
        } else {
          showToast(d.details || d.action || `Invoice ${action}`, true);
        }
        load();
      }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Action failed', false); }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadResult(null);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Pass the global entity selector so the backend doesn't fall
      // back to OCR'd entity strings (which may not match registered
      // entity_code) or to "first entity in registry" -- both have
      // landed invoices on the wrong entity historically.
      if (entity) formData.append('entity_code', entity);
      const res = await authFetch(`${API}/invoices/upload`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setUploadResult(data);
        showToast(`Invoice ${data.invoice_number} extracted + created`, true);
        load();
      } else {
        const e = await res.json();
        // Show inline in the modal (z-index-safe) AND as a toast.
        // Modal overlay sits at a higher z-index than the toast, so
        // an outside-modal toast can hide behind the backdrop.
        setUploadError(e.detail || `Upload failed (HTTP ${res.status})`);
        showToast(e.detail || 'Upload failed', false);
      }
    } catch (err: any) {
      setUploadError(err?.message || 'Upload failed (network or server error)');
      showToast('Upload failed', false);
    }
    setUploading(false);
  };

  const openEdit = (inv: Invoice) => {
    setEditInv(inv);
    setEditForm({ vendor_id: inv.vendor_id || '', entity_code: inv.entity_code || '', gl_account: inv.gl_account || '', payment_terms: inv.payment_terms || '', due_date: inv.due_date || '' });
    setEditLines((inv.lines || []).map(l => ({ id: l.id, gl_account: l.gl_account || '' })));
    setShowEdit(true);
  };

  const handleEdit = async () => {
    if (!editInv) return;
    try {
      const payload: any = { expected_version: editInv.version };
      if (editForm.vendor_id) payload.vendor_id = editForm.vendor_id;
      if (editForm.entity_code) payload.entity_code = editForm.entity_code;
      if (editForm.gl_account) payload.gl_account = editForm.gl_account;
      if (editForm.payment_terms) payload.payment_terms = editForm.payment_terms;
      if (editForm.due_date) payload.due_date = editForm.due_date;
      const changedLines = editLines.filter((el, i) => el.gl_account !== ((editInv.lines || [])[i]?.gl_account || ''));
      if (changedLines.length > 0) payload.line_updates = changedLines.map(l => ({ line_id: l.id, gl_account: l.gl_account }));
      const res = await authFetch(`${API}/invoices/${editInv.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Invoice updated', true); setShowEdit(false); load(); if (expandedId === editInv.id) toggleExpand(editInv.id); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Update failed', false); }
  };

  const handleAcceptVariance = async (id: number) => {
    try {
      const res = await authFetch(`${API}/invoices/${id}/accept-variance`, { method: 'POST' });
      if (res.ok) { showToast('Variance accepted — invoice moved to MATCHED', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleRecapitalize = async (id: number, invoice_number: string) => {
    try {
      const res = await authFetch(`${API_V1}/fixed-assets/recapitalize-invoice/${id}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        const n = d.count || 0;
        if (n > 0) {
          showToast(`Re-capitalized ${invoice_number} -- ${n} asset(s) created`, true);
        } else {
          showToast(`Re-capitalized ${invoice_number} -- no CAPEX lines to capitalize`, true);
        }
      } else {
        const e = await res.json().catch(() => ({}));
        const msg = typeof e.detail === 'string' ? e.detail : 'Re-capitalize failed';
        showToast(msg, false);
      }
    } catch { showToast('Re-capitalize failed', false); }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const selectAll = () => {
    if (selectedIds.size === invoices.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(invoices.map(i => i.id)));
  };

  const handleBatchAction = async (action: string) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await authFetch(`${API_V1}/ap/batch/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ invoice_ids: Array.from(selectedIds) }) });
      if (res.ok) { const d = await res.json(); showToast(`Batch ${action}: ${d.approved || d.posted || d.rejected || 0} processed`, true); setSelectedIds(new Set()); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Batch action failed', false); }
    } catch { showToast('Batch action failed', false); }
  };

  // Bulk action: take selected POSTED invoices -> create a Payment Run
  // in Treasury. The run starts in DRAFT; user navigates to Treasury
  // Payment Runs to submit-for-approval, approve, and execute (which
  // posts the DR AP / CR Cash JE and clears the invoice to PAID).
  const handleCreatePaymentRun = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    // Client-side guard: warn if any selected isn't POSTED. Server
    // will reject loudly too, but this stops the round-trip early.
    const notPosted = invoices.filter(i => selectedIds.has(i.id) && i.lifecycle_status !== 'POSTED');
    if (notPosted.length > 0) {
      showToast(
        `Cannot create payment run: ${notPosted.length} of ${ids.length} selected invoice(s) are not POSTED. Approve and post them first.`,
        false,
      );
      return;
    }
    if (!window.confirm(`Create a payment run for ${ids.length} selected invoice${ids.length === 1 ? '' : 's'}? You'll be taken to Treasury Payment Runs to submit for approval and execute.`)) {
      return;
    }
    try {
      const res = await authFetch(`${API_V1}/payments/runs/from-invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_ids: ids, payment_method: 'ACH' }),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Payment run #${d.id} created (${ids.length} invoices, ${formatAmount(d.total_amount || 0)}). Navigating to Treasury...`, true);
        setSelectedIds(new Set());
        // Brief delay so the toast is readable before navigation.
        setTimeout(() => navigate('/payment-runs'), 800);
      } else {
        const e = await res.json();
        showToast(e.detail || 'Payment run creation failed', false);
      }
    } catch {
      showToast('Payment run creation failed', false);
    }
  };

  const handleBatchUpload = async (files: FileList) => {
    setBatchUploading(true); setBatchResult(null);
    try {
      const fd = new FormData();
      for (let i = 0; i < files.length; i++) fd.append('files', files[i]);
      fd.append('auto_process', 'true');
      const res = await authFetch(`${API_V1}/ap/batch/upload`, { method: 'POST', body: fd });
      if (res.ok) { const d = await res.json(); setBatchResult(d); showToast(`Batch: ${d.created} created, ${d.pipeline?.auto_processed || 0} auto-processed`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Batch upload failed', false); }
    } catch { showToast('Batch upload failed', false); }
    setBatchUploading(false);
  };

  const toggleExpand = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setExpandedInv(null); return; }
    setExpandedId(id);
    try {
      const res = await authFetch(`${API}/invoices/${id}`);
      if (res.ok) { setExpandedInv(await res.json()); }
    } catch { setExpandedInv(null); }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };
  const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap', padding: '10px 14px' };
  const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };
  const labelStyle: React.CSSProperties = { fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 2 };

  return (
    <PageLayout title="AP Invoices" subtitle="Upload invoices, run GL coding, 3-way match, and approve for payment" action={{ label: 'Upload Invoice', onClick: () => setShowUpload(true) }}>
      {summary && (
        <KpiGrid items={[
          { label: 'Received', value: summary.received, color: 'var(--text-secondary)' },
          { label: 'Coded', value: summary.coded, color: 'var(--accent)' },
          { label: 'Matched', value: summary.matched, color: 'var(--positive)' },
          { label: 'Pending Approval', value: summary.pending_approval, color: 'var(--warning)' },
          { label: 'Posted', value: summary.posted, color: 'var(--positive)' },
          { label: 'Paid', value: summary.paid, color: 'var(--text-primary)' },
          { label: 'Pending $', value: formatAmountCompact(summary.pending_amount), color: 'var(--warning)' },
        ]} />
      )}

      <FilterBar filters={[
        { key: 'status', label: 'Statuses', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, options: ['RECEIVED', 'CODED', 'MATCHED', 'PENDING_APPROVAL', 'APPROVED', 'POSTED', 'PAID', 'VARIANCE_REVIEW'].map(s => ({ value: s, label: s })), width: 200 },
      ]} />

      {/* Batch Action Bar */}
      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 14px', background: 'var(--accent)', borderRadius: 8, alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{selectedIds.size} selected</span>
          <ActionButton label="Approve All" onClick={() => handleBatchAction('approve')} variant="success" size="sm" />
          <ActionButton label="Post All" onClick={() => handleBatchAction('post')} variant="primary" size="sm" />
          <ActionButton label="Pay Selected" onClick={handleCreatePaymentRun} variant="primary" size="sm" />
          <ActionButton label="Reject All" onClick={() => handleBatchAction('reject')} variant="secondary" size="sm" />
          <ActionButton label="Auto-Process" onClick={() => handleBatchAction('auto-process')} variant="secondary" size="sm" />
          <ActionButton label="Clear" onClick={() => setSelectedIds(new Set())} variant="ghost" size="sm" />
        </div>
      )}

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : invoices.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No invoices found.</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...thStyle, width: 36 }}><input type="checkbox" checked={selectedIds.size === invoices.length && invoices.length > 0} onChange={selectAll} /></th>
              {['Invoice #', 'Date', 'Vendor', 'PO', 'Amount', 'GL', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ ...thStyle, textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {invoices.map(inv => (
                <>
                  <tr key={inv.id} onClick={() => toggleExpand(inv.id)} style={{ cursor: 'pointer', background: expandedId === inv.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(inv.id)} onChange={() => toggleSelect(inv.id)} /></td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{inv.invoice_number}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 12 }}>{inv.invoice_date || '-'}</td>
                    <td style={tdStyle}>{inv.vendor_name || inv.vendor_id || '-'}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{inv.po_number || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(inv.gross_amount)}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 12 }}>{inv.gl_account || '-'}</td>
                    <td style={tdStyle}><StatusBadge status={inv.lifecycle_status} /></td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {inv.lifecycle_status === 'RECEIVED' && <ActionButton label="Code" onClick={() => handleAction(inv.id, 'code')} variant="secondary" size="sm" />}
                        {(inv.lifecycle_status === 'CODED' || inv.lifecycle_status === 'RECEIVED') && <ActionButton label="Match" onClick={() => handleAction(inv.id, 'match')} variant="secondary" size="sm" />}
                        {inv.lifecycle_status === 'VARIANCE_REVIEW' && <ActionButton label="Accept" onClick={() => handleAcceptVariance(inv.id)} variant="secondary" size="sm" />}
                        {inv.lifecycle_status === 'MATCHED' && <ActionButton label="Route" onClick={() => handleAction(inv.id, 'route-approval')} variant="secondary" size="sm" />}
                        {inv.lifecycle_status === 'PENDING_APPROVAL' && <ActionButton label="Approve" onClick={() => handleAction(inv.id, 'approve', inv.version)} variant="success" size="sm" disabled={!canApprove(inv.gross_amount || 0, inv.entity_code, 'AP_INVOICE')} />}
                        {inv.lifecycle_status === 'APPROVED' && <ActionButton label="Post JE" onClick={() => handleAction(inv.id, 'post', inv.version)} variant="primary" size="sm" />}
                        {inv.lifecycle_status === 'POSTED' && inv.has_unlinked_capex && <ActionButton label="Re-capitalize" onClick={() => handleRecapitalize(inv.id, inv.invoice_number)} variant="secondary" size="sm" title="One or more CAPEX lines on this invoice are not linked to a Fixed Asset. Click to create the missing assets." />}
                        {!['POSTED','PAID'].includes(inv.lifecycle_status) && <ActionButton label="Edit" onClick={() => openEdit(inv)} variant="ghost" size="sm" />}
                      </div>
                    </td>
                  </tr>

                  {/* Expandable Detail Row */}
                  {expandedId === inv.id && expandedInv && (
                    <tr key={`${inv.id}-detail`}><td colSpan={9} style={{ padding: '0 14px 16px 14px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>

                        {/* Invoice Details Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                          {[
                            { label: 'Invoice #', value: expandedInv.invoice_number },
                            { label: 'Vendor ID', value: expandedInv.vendor_id || 'Not linked' },
                            { label: 'Entity', value: expandedInv.entity_code || '-' },
                            { label: 'Payment Terms', value: expandedInv.payment_terms || '-' },
                            { label: 'Due Date', value: expandedInv.due_date || '-' },
                            { label: 'Currency', value: expandedInv.currency || 'USD' },
                          ].map(f => (
                            <div key={f.label} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
                              <div style={labelStyle}>{f.label}</div>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{f.value}</div>
                            </div>
                          ))}
                        </div>

                        {/* 3-Way Match Result */}
                        {expandedInv.match_result && (
                          <div style={{ padding: 12, borderRadius: 8, background: expandedInv.match_status === 'FULL_MATCH' ? '#f0fdf4' : '#fff7ed', border: `1px solid ${expandedInv.match_status === 'FULL_MATCH' ? '#22c55e' : '#f59e0b'}` }}>
                            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>3-Way Match: <StatusBadge status={expandedInv.match_status || 'UNKNOWN'} /></div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 6 }}>
                              {[
                                { label: 'Invoice Amount', value: formatAmount(expandedInv.match_result.invoice_amount) },
                                { label: 'Expected (PO x GR)', value: formatAmount(expandedInv.match_result.expected_amount) },
                                { label: 'PO Amount', value: formatAmount(expandedInv.match_result.po_amount) },
                                { label: 'Price Variance', value: `${(expandedInv.price_variance_pct || 0).toFixed(1)}%` },
                                { label: 'Qty Variance', value: `${(expandedInv.qty_variance_pct || 0).toFixed(1)}%` },
                              ].map(f => (
                                <div key={f.label}>
                                  <div style={labelStyle}>{f.label}</div>
                                  <div style={{ fontSize: 13, fontWeight: 600 }}>{f.value}</div>
                                </div>
                              ))}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{expandedInv.match_result.details}</div>
                            {expandedInv.match_result.gr_count !== undefined && (
                              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                                GR count: {expandedInv.match_result.gr_count} | GR qty: {expandedInv.match_result.gr_total_qty} | PO qty: {expandedInv.match_result.po_total_qty}
                              </div>
                            )}
                          </div>
                        )}
                        {!expandedInv.match_result && expandedInv.match_status && (
                          <div style={{ padding: 10, borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>Match Status: <StatusBadge status={expandedInv.match_status} /></div>
                          </div>
                        )}

                        {/* Approval Trail */}
                        <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Approval Trail</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            <div><div style={labelStyle}>Approval Level</div><div style={{ fontSize: 12 }}>{expandedInv.approval_level || 'Pending'}</div></div>
                            <div><div style={labelStyle}>Approved By</div><div style={{ fontSize: 12 }}>{expandedInv.approved_by === 0 ? 'System (Auto)' : expandedInv.approved_by ? `User #${expandedInv.approved_by}` : 'Pending'}</div></div>
                            <div><div style={labelStyle}>Approved At</div><div style={{ fontSize: 12 }}>{expandedInv.approved_at ? new Date(expandedInv.approved_at).toLocaleString() : '-'}</div></div>
                            <div><div style={labelStyle}>Posted Date</div><div style={{ fontSize: 12 }}>{expandedInv.posting_date || '-'}</div></div>
                          </div>
                        </div>

                        {/* Linked GRs */}
                        {expandedInv.linked_grs && expandedInv.linked_grs.length > 0 && (
                          <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Linked Goods Receipts ({expandedInv.linked_grs.length})</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {expandedInv.linked_grs.map((gr: any) => (
                                <div key={gr.id} style={{ padding: '6px 10px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', fontSize: 11 }}>
                                  <span style={{ fontWeight: 600 }}>{gr.gr_number?.substring(0, 25)}</span>
                                  <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>Qty: {gr.quantity} | {formatAmount(gr.amount)}</span>
                                  <span style={{ marginLeft: 6 }}><StatusBadge status={gr.status} /></span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Payment Link */}
                        {expandedInv.payment && (
                          <div style={{ padding: 10, borderRadius: 6, background: '#f0fdf4', border: '1px solid #22c55e' }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>
                              Payment Run #{expandedInv.payment.run_id} — <StatusBadge status={expandedInv.payment.status} />
                              <span style={{ color: 'var(--text-secondary)', fontSize: 11, marginLeft: 8 }}>Item: {expandedInv.payment.item_status} | Date: {expandedInv.payment.run_date}</span>
                            </div>
                          </div>
                        )}

                        {/* Line Items with GL + PO match */}
                        {expandedInv.lines && expandedInv.lines.length > 0 && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, color: 'var(--text-secondary)' }}>LINE ITEMS ({expandedInv.lines.length})</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                {['#', 'Description', 'Qty', 'Unit Price', 'Inv Amount', 'GL Account', 'PO GL', 'Line Var'].map(h => (
                                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>{expandedInv.lines.map((li: InvoiceLine) => {
                                const lm = expandedInv.match_result?.line_matches?.find((m: any) => m.line_number === li.line_number);
                                return (
                                  <tr key={li.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    <td style={{ padding: '6px 8px' }}>{li.line_number}</td>
                                    <td style={{ padding: '6px 8px' }}>{(li.description || '').substring(0, 55)}</td>
                                    <td style={{ padding: '6px 8px' }}>{li.quantity}</td>
                                    <td style={{ padding: '6px 8px' }}>{li.unit_price ? formatAmount(li.unit_price) : '-'}</td>
                                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{li.line_amount ? formatAmount(li.line_amount) : '-'}</td>
                                    <td style={{ padding: '6px 8px', color: li.gl_account ? 'var(--accent)' : 'var(--text-hint)' }}>{li.gl_account || '-'}</td>
                                    <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{lm?.po_gl || '-'}</td>
                                    <td style={{ padding: '6px 8px', color: lm && lm.variance_pct > 2 ? 'var(--negative)' : 'var(--positive)' }}>{lm?.matched ? `${lm.variance_pct}%` : '-'}</td>
                                  </tr>
                                );
                              })}</tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      {/* Upload Invoice Modal */}
      <Modal open={showUpload} onClose={() => { setShowUpload(false); setUploadResult(null); setBatchResult(null); setUploadError(null); }} title="Upload Vendor Invoice" size="lg" footer={
        (uploadResult || batchResult) ? (
          <><ActionButton label="Done" onClick={() => { setShowUpload(false); setUploadResult(null); setBatchResult(null); setUploadError(null); }} variant="success" /></>
        ) : (
          <><ActionButton label="Cancel" onClick={() => { setShowUpload(false); setUploadError(null); }} variant="secondary" />
          <ActionButton label="Manual Entry" onClick={() => { setShowUpload(false); setUploadError(null); setShowCreate(true); }} variant="secondary" /></>
        )
      }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Inline error banner -- always visible inside the modal so it
              isn't hidden behind the modal overlay like the outside-modal
              toast (z-index race). Persists until user dismisses or retries. */}
          {uploadError && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: 'var(--negative-bg)', color: 'var(--negative)',
              border: '1px solid var(--negative)',
              fontSize: 13, lineHeight: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Upload failed</div>
                {uploadError}
              </div>
              <button
                type="button"
                onClick={() => setUploadError(null)}
                aria-label="Dismiss error"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'inherit', fontSize: 18, fontWeight: 700,
                  lineHeight: 1, padding: 0, marginTop: -2,
                }}
              >×</button>
            </div>
          )}
          {!uploadResult && !uploading && (
            <div style={{ border: '2px dashed var(--border-default)', borderRadius: 12, padding: 40, textAlign: 'center', background: 'var(--bg-card)', cursor: 'pointer' }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--border-default)'; if (e.dataTransfer.files.length > 1) { handleBatchUpload(e.dataTransfer.files); } else { const f = e.dataTransfer.files[0]; if (f) handleUpload(f); } }}
              onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.accept = '.pdf,.png,.jpg,.jpeg'; inp.multiple = true; inp.onchange = (e: any) => { if (e.target.files?.length > 1) { handleBatchUpload(e.target.files); } else { const f = e.target.files?.[0]; if (f) handleUpload(f); } }; inp.click(); }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>+</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Drop invoice PDFs here or click to browse</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>Supports PDF, PNG, JPG (max 10 MB each) — select multiple files for batch processing</div>
              <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 8 }}>AI extracts + auto-processes: GL code, 3-way match, approve, post (within DOA limits)</div>
            </div>
          )}

          {(uploading || batchUploading) && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}>{batchUploading ? 'Processing batch with AI Vision...' : 'Extracting invoice data with AI Vision...'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{batchUploading ? 'Each file is extracted, duplicate-checked, and auto-processed' : 'This may take 10-15 seconds for multi-page PDFs'}</div>
            </div>
          )}

          {batchResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ padding: 12, borderRadius: 8, background: '#f0fdf4', border: '1px solid #22c55e' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Batch Processing Complete</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                {[
                  { label: 'Total Files', value: batchResult.total_files, color: 'var(--text-primary)' },
                  { label: 'Created', value: batchResult.created, color: 'var(--positive)' },
                  { label: 'Auto-Processed', value: batchResult.pipeline?.auto_processed || 0, color: 'var(--accent)' },
                  { label: 'Duplicates', value: batchResult.duplicates, color: 'var(--warning)' },
                  { label: 'Failed', value: (batchResult.extraction_failed || 0) + (batchResult.errors || 0), color: 'var(--negative)' },
                ].map(k => (
                  <div key={k.label} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' }}>{k.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: k.color }}>{k.value}</div>
                  </div>
                ))}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  {['File', 'Status', 'Invoice #', 'Vendor', 'Amount', 'Pipeline'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{(batchResult.files || []).map((f: any, i: number) => {
                  const pipelineResult = batchResult.pipeline?.results?.find((r: any) => r.invoice_id === f.invoice_id);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)', background: f.status === 'DUPLICATE' ? '#fff7ed' : f.status === 'ERROR' ? '#fef2f2' : 'transparent' }}>
                      <td style={{ padding: '6px 8px' }}>{(f.filename || '').substring(0, 30)}</td>
                      <td style={{ padding: '6px 8px' }}><StatusBadge status={f.status} /></td>
                      <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--accent)' }}>{f.invoice_number || '-'}</td>
                      <td style={{ padding: '6px 8px' }}>{f.vendor_name || '-'}</td>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{f.amount ? formatAmount(f.amount) : '-'}</td>
                      <td style={{ padding: '6px 8px' }}>{pipelineResult ? (pipelineResult.auto_processed ? 'Auto-Processed' : pipelineResult.final_status) : (f.status === 'DUPLICATE' ? 'Blocked' : '-')}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}

          {uploadResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ padding: 12, borderRadius: 8, background: 'var(--positive-bg)', border: '1px solid var(--positive)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--positive)' }}>Invoice extracted and created successfully</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {[
                  { label: 'Invoice #', value: uploadResult.invoice_number },
                  { label: 'Vendor', value: uploadResult.vendor_name },
                  { label: 'Amount', value: formatAmount(uploadResult.gross_amount) },
                  { label: 'PO Reference', value: uploadResult.po_number || '-' },
                  { label: 'Entity', value: uploadResult.entity_code || '-' },
                  { label: 'Vendor ID', value: uploadResult.vendor_id || 'Not linked' },
                ].map(f => (
                  <div key={f.label} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' }}>{f.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{f.value}</div>
                  </div>
                ))}
              </div>

              {uploadResult.match_result && (
                <div style={{ padding: 10, borderRadius: 6, background: uploadResult.match_result.match_status === 'FULL_MATCH' ? '#f0fdf4' : '#fff7ed', border: `1px solid ${uploadResult.match_result.match_status === 'FULL_MATCH' ? '#22c55e' : '#f59e0b'}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>3-Way Match: {uploadResult.match_result.match_status}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{uploadResult.match_result.details}</div>
                  {uploadResult.match_result.price_variance_pct !== undefined && (
                    <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                      Price var: {uploadResult.match_result.price_variance_pct?.toFixed(1)}% | Qty var: {uploadResult.match_result.qty_variance_pct?.toFixed(1)}% | GRs: {uploadResult.match_result.gr_count}
                    </div>
                  )}
                </div>
              )}

              {uploadResult.extracted_fields?.line_items && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, color: 'var(--text-secondary)' }}>EXTRACTED LINE ITEMS ({uploadResult.extracted_fields.line_items.length})</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      {['#', 'Description', 'Qty', 'Price', 'Amount'].map(h => <th key={h} style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, fontWeight: 700, color: 'var(--text-hint)' }}>{h}</th>)}
                    </tr></thead>
                    <tbody>{uploadResult.extracted_fields.line_items.map((li: any, i: number) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '4px 6px' }}>{li.line_number || i + 1}</td>
                        <td style={{ padding: '4px 6px' }}>{(li.description || '').substring(0, 60)}</td>
                        <td style={{ padding: '4px 6px' }}>{li.quantity}</td>
                        <td style={{ padding: '4px 6px' }}>{formatAmount(li.unit_price || 0)}</td>
                        <td style={{ padding: '4px 6px', fontWeight: 600 }}>{formatAmount(li.amount || 0)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={`Edit Invoice ${editInv?.invoice_number || ''}`} size="md" footer={<><ActionButton label="Cancel" onClick={() => setShowEdit(false)} variant="secondary" /><ActionButton label="Save Changes" onClick={handleEdit} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><div style={labelStyle}>Vendor ID</div><input value={editForm.vendor_id} onChange={e => setEditForm({ ...editForm, vendor_id: e.target.value })} style={inputStyle} /></div>
            <div style={{ flex: 1 }}><div style={labelStyle}>Entity Code</div>
              <select value={editForm.entity_code} onChange={e => setEditForm({ ...editForm, entity_code: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Select</option>
                {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><div style={labelStyle}>Header GL Account</div><input value={editForm.gl_account} onChange={e => setEditForm({ ...editForm, gl_account: e.target.value })} style={inputStyle} /></div>
            <div style={{ flex: 1 }}><div style={labelStyle}>Payment Terms</div><input value={editForm.payment_terms} onChange={e => setEditForm({ ...editForm, payment_terms: e.target.value })} style={inputStyle} /></div>
          </div>
          <div><div style={labelStyle}>Due Date</div><input type="date" value={editForm.due_date} onChange={e => setEditForm({ ...editForm, due_date: e.target.value })} style={inputStyle} /></div>

          {editLines.length > 0 && (
            <div>
              <div style={{ ...labelStyle, marginBottom: 4 }}>Line GL Accounts</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  {['#', 'Description', 'GL Account'].map(h => <th key={h} style={{ textAlign: 'left', padding: '4px 6px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)' }}>{h}</th>)}
                </tr></thead>
                <tbody>{editLines.map((el, i) => (
                  <tr key={el.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '4px 6px' }}>{(editInv?.lines || [])[i]?.line_number}</td>
                    <td style={{ padding: '4px 6px' }}>{((editInv?.lines || [])[i]?.description || '').substring(0, 50)}</td>
                    <td style={{ padding: '4px 6px' }}><input value={el.gl_account} onChange={e => { const nl = [...editLines]; nl[i] = { ...nl[i], gl_account: e.target.value }; setEditLines(nl); }} style={{ ...inputStyle, padding: '4px 8px' }} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* Manual Entry Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Enter AP Invoice (Manual)" size="md" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create Invoice" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Legal Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name} ({e.functional_currency})</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}><input placeholder="Invoice Number" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} style={inputStyle} /><input placeholder="Invoice Date (YYYY-MM-DD)" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} style={inputStyle} /></div>
          <div style={{ display: 'flex', gap: 8 }}><input placeholder="Vendor ID" value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} style={inputStyle} /><input placeholder="Vendor Name" value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} style={inputStyle} /></div>
          <div style={{ display: 'flex', gap: 8 }}><input placeholder="PO Number (optional)" value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })} style={inputStyle} /><input placeholder="Gross Amount" type="number" value={form.gross_amount} onChange={e => setForm({ ...form, gross_amount: e.target.value })} style={inputStyle} /></div>
          <div style={{ display: 'flex', gap: 8 }}><input placeholder="Currency" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={inputStyle} /></div>
          <input placeholder="Payment Terms (e.g. NET30)" value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} style={inputStyle} />
        </div>
      </Modal>

      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 2000,
          padding: '12px 16px 12px 20px', borderRadius: 8,
          background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)',
          color: toast.ok ? 'var(--positive)' : 'var(--negative)',
          fontSize: 13, fontWeight: 600,
          maxWidth: 520, lineHeight: 1.4,
          display: 'flex', alignItems: 'flex-start', gap: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{toast.msg}</span>
          {!toast.ok && (
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="Dismiss"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'inherit', fontSize: 18, fontWeight: 700,
                lineHeight: 1, padding: 0, marginTop: -2,
              }}
            >×</button>
          )}
        </div>
      )}
    </PageLayout>
  );
}
